import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { EmptyState } from "@/components/app/ui-bits";
import { PresentationChart, Scales as ScalesIcon, Waves, ChartLineUp, BookOpenText } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const money = (v) => (v || v === 0 ? formatCurrency(v) : "—");
const yearNow = new Date().getFullYear();

// Drill-down: every posted journal line behind one statement figure.
function AccountLedgerDialog({ drill, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!drill) { setData(null); return; }
    api.get(`/finance/gl/accounts/${drill.code}/ledger`, { params: drill.params || {} })
      .then(({ data }) => setData(data))
      .catch((e) => { toast.error(formatApiError(e)); setData({ lines: [] }); });
  }, [drill]);
  return (
    <Dialog open={!!drill} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="account-ledger-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <BookOpenText size={18} className="text-[#1E5CBA]" weight="fill" />
            <span className="font-mono-alt">{drill?.code}</span> · {data?.account?.name || drill?.name || ""}
          </DialogTitle>
          <DialogDescription>Account ledger — every posted journal line behind this statement figure.</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="font-mono-alt text-xs uppercase tracking-widest text-slate-400">Loading…</div>
        ) : (data.lines || []).length === 0 ? (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-500" data-testid="account-ledger-empty">
            No posted journal lines for this account in the selected period.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-4 text-xs">
              <span>Total debits <b className="font-mono-alt tabular-nums">{money(data.total_debit)}</b></span>
              <span>Total credits <b className="font-mono-alt tabular-nums">{money(data.total_credit)}</b></span>
              <span>Closing (Dr−Cr) <b className="font-mono-alt tabular-nums" data-testid="account-ledger-closing">{money(data.closing_balance)}</b></span>
            </div>
            <div className="max-h-[52vh] overflow-y-auto rounded-md border border-slate-200">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="text-left text-[9px] uppercase tracking-wider text-slate-400">
                    <th className="px-3 py-1.5 font-semibold">Date</th>
                    <th className="px-3 py-1.5 font-semibold">Journal</th>
                    <th className="px-3 py-1.5 font-semibold">Description</th>
                    <th className="px-3 py-1.5 text-right font-semibold">Debit</th>
                    <th className="px-3 py-1.5 text-right font-semibold">Credit</th>
                    <th className="px-3 py-1.5 text-right font-semibold">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lines.map((ln, i) => (
                    <tr key={ln.id || i} className="border-t border-slate-100" data-testid={`ledger-line-${i}`}>
                      <td className="px-3 py-1 font-mono-alt tabular-nums text-slate-500">{formatDate(ln.date)}</td>
                      <td className="px-3 py-1 font-mono-alt font-semibold text-slate-900">{ln.entry_no}</td>
                      <td className="px-3 py-1 text-slate-600">{ln.description || ln.memo || "—"}</td>
                      <td className="px-3 py-1 text-right font-mono-alt tabular-nums">{ln.debit ? money(ln.debit) : ""}</td>
                      <td className="px-3 py-1 text-right font-mono-alt tabular-nums">{ln.credit ? money(ln.credit) : ""}</td>
                      <td className="px-3 py-1 text-right font-mono-alt tabular-nums font-semibold">{money(ln.running_balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-[10px] text-slate-400">Full journals live in Finance → General Ledger → Journals.</div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SectionTable({ title, rows, total, totalLabel, negative, onRow }) {
  if (!rows?.length) return null;
  return (
    <div>
      <div className="overline mb-1">{title}</div>
      <table className="data-table w-full">
        <tbody>
          {rows.map((r) => (
            <tr key={r.code + r.name}
                className={onRow ? "cursor-pointer transition-colors hover:bg-blue-50/60" : ""}
                onClick={onRow ? () => onRow(r) : undefined}
                title={onRow ? "Click to open this account's ledger" : undefined}
                data-testid={`stmt-row-${r.code}`}>
              <td className="font-mono-alt text-xs tabular-nums w-16">{r.code}</td>
              <td className="text-xs text-slate-800">{r.name}</td>
              <td className="text-right font-mono-alt tabular-nums text-xs">{money(r.amount ?? r.balance)}</td>
            </tr>
          ))}
          <tr className="font-bold border-t-2 border-slate-300">
            <td /><td className="text-xs">{totalLabel}</td>
            <td className={`text-right font-mono-alt tabular-nums text-xs ${negative ? "text-rose-600" : ""}`}>{money(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function GLIncomeStatement({ data, onRow }) {
  if (!data) return null;
  return (
    <div className="space-y-5" data-testid="stmt-is-gl">
      <SectionTable title="Revenue" rows={data.revenue} total={data.total_revenue} totalLabel="Total revenue" onRow={onRow} />
      <SectionTable title="Project costs (direct)" rows={data.project_costs} total={data.total_project_costs} totalLabel="Total project costs" negative onRow={onRow} />
      <SectionTable title="Overhead (indirect)" rows={data.overhead} total={data.total_overhead} totalLabel="Total overhead" negative onRow={onRow} />
      <div className="dc-card p-4 grid grid-cols-3 gap-3">
        <div><div className="overline">Gross profit</div><div className="font-display text-lg font-bold tabular-nums">{money(data.gross_profit)}</div></div>
        <div><div className="overline">Net income</div><div className={`font-display text-lg font-bold tabular-nums ${data.net_income >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="stmt-is-net">{money(data.net_income)}</div></div>
        <div><div className="overline">Margin</div><div className="font-display text-lg font-bold tabular-nums">{data.margin_pct}%</div></div>
      </div>
    </div>
  );
}

function GLBalanceSheet({ data, onRow }) {
  if (!data) return null;
  return (
    <div className="space-y-5" data-testid="stmt-bs-gl">
      <div className={`rounded-md border px-3 py-2 text-xs font-semibold ${data.balanced ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"}`} data-testid="stmt-bs-balanced">
        {data.balanced ? "BALANCED ✓" : "NOT BALANCED"} · Assets {money(data.total_assets)} = Liabilities {money(data.total_liabilities)} + Equity {money(data.total_equity)}
      </div>
      <SectionTable title="Assets" rows={data.assets} total={data.total_assets} totalLabel="Total assets" onRow={onRow} />
      <SectionTable title="Liabilities" rows={data.liabilities} total={data.total_liabilities} totalLabel="Total liabilities" onRow={onRow} />
      <SectionTable title="Equity" rows={data.equity} total={data.total_equity} totalLabel="Total equity" onRow={onRow} />
    </div>
  );
}

function GLCashFlow({ data }) {
  if (!data) return null;
  const secs = [["operating", "Operating activities"], ["investing", "Investing activities"], ["financing", "Financing activities"]];
  return (
    <div className="space-y-5" data-testid="stmt-cf-gl">
      <div className="grid grid-cols-3 gap-3">
        <div className="dc-card p-3"><div className="overline">Opening cash</div><div className="font-display text-lg font-bold tabular-nums">{money(data.opening_cash)}</div></div>
        <div className="dc-card p-3"><div className="overline">Net change</div><div className={`font-display text-lg font-bold tabular-nums ${data.net_change >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{money(data.net_change)}</div></div>
        <div className="dc-card p-3"><div className="overline">Closing cash</div><div className="font-display text-lg font-bold tabular-nums" data-testid="stmt-cf-closing">{money(data.closing_cash)}</div></div>
      </div>
      {secs.map(([k, label]) => {
        const s = data.sections?.[k];
        if (!s?.items?.length) return null;
        return (
          <div key={k}>
            <div className="overline mb-1">{label}</div>
            <table className="data-table w-full">
              <tbody>
                {s.items.map((it) => (
                  <tr key={it.label}>
                    <td className="text-xs text-slate-800">{it.label}</td>
                    <td className={`text-right font-mono-alt tabular-nums text-xs ${it.amount < 0 ? "text-rose-600" : "text-emerald-700"}`}>{money(it.amount)}</td>
                  </tr>
                ))}
                <tr className="font-bold border-t-2 border-slate-300">
                  <td className="text-xs">Net cash from {label.toLowerCase()}</td>
                  <td className="text-right font-mono-alt tabular-nums text-xs">{money(s.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

// ── Legacy renderers ──
function LegacyIS({ audit }) {
  const rows = audit?.income_statement || [];
  if (!rows.length) return <EmptyState icon={PresentationChart} title="No legacy income data" description="" />;
  return (
    <div data-testid="stmt-is-legacy">
      <table className="data-table w-full">
        <thead><tr><th>Year</th><th className="text-right">Revenue</th><th className="text-right">Project costs</th><th className="text-right">Overhead</th><th className="text-right">Net</th><th className="text-right">Margin</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.year}>
              <td className="font-mono-alt text-xs">{r.year}</td>
              <td className="text-right font-mono-alt tabular-nums text-xs">{money(r.revenue)}</td>
              <td className="text-right font-mono-alt tabular-nums text-xs">{money(Math.abs(r.project_cost))}</td>
              <td className="text-right font-mono-alt tabular-nums text-xs">{money(Math.abs(r.overhead))}</td>
              <td className="text-right font-mono-alt tabular-nums text-xs font-semibold">{money(r.revenue - Math.abs(r.project_cost) - Math.abs(r.overhead))}</td>
              <td className="text-right font-mono-alt tabular-nums text-xs">{r.revenue ? (((r.revenue - Math.abs(r.project_cost) - Math.abs(r.overhead)) / r.revenue) * 100).toFixed(1) : 0}%</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2 text-[11px] text-slate-400">Legacy source: flat finance_entries aggregation (audit-figures). Costs shown as absolute values.</div>
    </div>
  );
}

function LegacyBS({ audit }) {
  const bs = audit?.balance_sheet;
  if (!bs) return <EmptyState icon={ScalesIcon} title="No legacy balance sheet" description="" />;
  const rows = [
    ["Cash & equivalents", bs.assets?.cash_and_equivalents],
    ["Accounts receivable", bs.assets?.accounts_receivable],
    ["Fixed assets (NBV)", bs.assets?.fixed_assets_nbv],
    ["TOTAL ASSETS", bs.assets?.total],
    ["Accounts payable", bs.liabilities?.accounts_payable],
    ["TOTAL LIABILITIES", bs.liabilities?.total],
    ["Retained earnings (inferred)", bs.equity?.retained_earnings],
    ["TOTAL EQUITY", bs.equity?.total],
  ];
  return (
    <div data-testid="stmt-bs-legacy">
      <table className="data-table w-full">
        <tbody>
          {rows.map(([l, v]) => (
            <tr key={l} className={l.startsWith("TOTAL") ? "font-bold" : ""}>
              <td className="text-xs">{l}</td>
              <td className="text-right font-mono-alt tabular-nums text-xs">{money(v || 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2 text-[11px] text-slate-400">Legacy source: subledger snapshot (bank recons + AR/AP + assets); equity is inferred as the balancing figure.</div>
    </div>
  );
}

function LegacyCF({ cf }) {
  if (!cf) return <EmptyState icon={Waves} title="No legacy cash flow" description="" />;
  return (
    <div data-testid="stmt-cf-legacy">
      <div className="dc-card p-3 mb-3"><div className="overline">Opening balance (reconciled)</div><div className="font-display text-lg font-bold tabular-nums">{money(cf.opening_balance)}</div></div>
      <table className="data-table w-full">
        <thead><tr><th>Month</th><th className="text-right">Inflows</th><th className="text-right">Outflows</th><th className="text-right">Net</th></tr></thead>
        <tbody>
          {(cf.rows || []).map((r) => {
            const inn = Object.values(r.inflows || {}).reduce((a, b) => a + (b || 0), 0);
            const out = Object.values(r.outflows || {}).reduce((a, b) => a + (b || 0), 0) + (r.tax_outflow || 0);
            return (
              <tr key={r.period}>
                <td className="font-mono-alt text-xs">{r.period}</td>
                <td className="text-right font-mono-alt tabular-nums text-xs">{money(inn)}</td>
                <td className="text-right font-mono-alt tabular-nums text-xs">{money(out)}</td>
                <td className="text-right font-mono-alt tabular-nums text-xs font-semibold">{money(inn - out)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-2 text-[11px] text-slate-400">Legacy source: 3-month cash-flow FORECAST (manually saved projections), not historical actuals.</div>
    </div>
  );
}

const VIEWS = [
  { v: "is", l: "Income Statement", i: PresentationChart },
  { v: "bs", l: "Balance Sheet", i: ScalesIcon },
  { v: "cf", l: "Cash Flow", i: Waves },
  { v: "eq", l: "Equity Changes", i: ChartLineUp },
];

function GLEquityChanges({ data, onRow }) {
  if (!data || !Array.isArray(data.rows)) return null;
  return (
    <div data-testid="stmt-eq-gl">
      <table className="data-table w-full">
        <thead><tr><th>Account</th><th className="text-right">Opening</th><th className="text-right">Movement</th><th className="text-right">Closing</th></tr></thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.code + r.name}
                className={onRow && r.code !== "\u2014" ? "cursor-pointer transition-colors hover:bg-blue-50/60" : ""}
                onClick={onRow && r.code !== "\u2014" ? () => onRow(r) : undefined}
                data-testid={`stmt-row-${r.code}`}>
              <td className="text-xs">{r.code !== "\u2014" && <span className="font-mono-alt mr-1">{r.code}</span>}{r.name}</td>
              <td className="text-right font-mono-alt tabular-nums text-xs">{money(r.opening)}</td>
              <td className={`text-right font-mono-alt tabular-nums text-xs ${r.movement < 0 ? "text-rose-600" : r.movement > 0 ? "text-emerald-700" : ""}`}>{money(r.movement)}</td>
              <td className="text-right font-mono-alt tabular-nums text-xs font-semibold">{money(r.closing)}</td>
            </tr>
          ))}
          <tr className="font-bold border-t-2 border-slate-300">
            <td className="text-xs">TOTAL EQUITY</td>
            <td className="text-right font-mono-alt tabular-nums text-xs">{money(data.total_opening)}</td>
            <td className="text-right font-mono-alt tabular-nums text-xs">{money(data.total_movement)}</td>
            <td className="text-right font-mono-alt tabular-nums text-xs" data-testid="stmt-eq-total">{money(data.total_closing)}</td>
          </tr>
        </tbody>
      </table>
      <div className="mt-2 text-[11px] text-slate-400">Statement of Changes in Equity — opening balances, in-year movements (incl. year-end close postings) and unclosed current-year earnings.</div>
    </div>
  );
}

export default function GLStatementsTab() {
  const [view, setView] = useState("is");
  const [source, setSource] = useState("gl");
  const [fromPeriod, setFromPeriod] = useState(`${yearNow}-01`);
  const [toPeriod, setToPeriod] = useState(`${yearNow}-12`);
  const [asOf, setAsOf] = useState("");
  const [fy, setFy] = useState(yearNow);
  const [gl, setGl] = useState(null);
  const [legacyAudit, setLegacyAudit] = useState(null);
  const [legacyCf, setLegacyCf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null);

  // Statement row → account ledger drill (same period window as the statement)
  const openDrill = (r) => {
    if (!r?.code || r.code === "\u2014") return;
    const params = view === "bs" ? (asOf ? { to_period: asOf } : {})
      : view === "eq" ? { from_period: `${fy}-01`, to_period: `${fy}-12` }
      : { ...(fromPeriod && { from_period: fromPeriod }), ...(toPeriod && { to_period: toPeriod }) };
    setDrill({ code: r.code, name: r.name, params });
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = view === "bs" ? (asOf ? { as_of_period: asOf } : {})
        : view === "eq" ? { fiscal_year: fy }
        : { ...(fromPeriod && { from_period: fromPeriod }), ...(toPeriod && { to_period: toPeriod }) };
      const glUrl = view === "is" ? "/finance/gl/statements/income-statement"
        : view === "bs" ? "/finance/gl/statements/balance-sheet"
        : view === "eq" ? "/finance/gl/statements/equity-changes" : "/finance/gl/statements/cash-flow";
      const [glRes, auditRes, cfRes] = await Promise.all([
        api.get(glUrl, { params }),
        api.get("/finance/audit-figures").catch(() => ({ data: null })),
        view === "cf" ? api.get("/finance/cashflow").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      ]);
      setGl(glRes.data);
      setLegacyAudit(auditRes.data);
      setLegacyCf(cfRes.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  // comparison strip numbers
  const cmp = (() => {
    if (!gl || !legacyAudit) return null;
    if (view === "is") {
      const y = legacyAudit.income_statement?.find((r) => r.year === String(yearNow));
      const legacyNet = y ? y.revenue - Math.abs(y.project_cost) - Math.abs(y.overhead) : null;
      return legacyNet == null ? null : { label: `Net income ${yearNow}`, gl: gl.net_income, legacy: legacyNet };
    }
    if (view === "bs") {
      const legacyTa = legacyAudit.balance_sheet?.assets?.total;
      return legacyTa == null ? null : { label: "Total assets", gl: gl.total_assets, legacy: legacyTa };
    }
    if (view === "cf" && legacyCf) {
      return { label: "Cash position", gl: gl.closing_cash, legacy: legacyCf.opening_balance };
    }
    return null;
  })();

  return (
    <div className="space-y-4" data-testid="gl-statements-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1">
          {VIEWS.map((t) => (
            <button key={t.v} onClick={() => { setGl(null); setView(t.v); }} data-testid={`stmt-view-${t.v}`}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${view === t.v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              <t.i size={13} /> {t.l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 rounded-full border border-slate-200 p-0.5" data-testid="stmt-source-toggle">
          {[["gl", "GL Ledger"], ["legacy", "Legacy"]].map(([v, l]) => (
            <button key={v} onClick={() => setSource(v)} data-testid={`stmt-source-${v}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${source === v ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-800"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {source === "gl" && (
        <div className="flex flex-wrap items-end gap-2">
          {view === "bs" ? (
            <div>
              <Label className="text-xs">As of period</Label>
              <Input type="month" className="h-8 w-40" value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="stmt-asof" />
            </div>
          ) : view === "eq" ? (
            <div>
              <Label className="text-xs">Fiscal year</Label>
              <Input type="number" className="h-8 w-28" value={fy} onChange={(e) => setFy(parseInt(e.target.value) || yearNow)} data-testid="stmt-fy" />
            </div>
          ) : (
            <>
              <div>
                <Label className="text-xs">From</Label>
                <Input type="month" className="h-8 w-40" value={fromPeriod} onChange={(e) => setFromPeriod(e.target.value)} data-testid="stmt-from" />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="month" className="h-8 w-40" value={toPeriod} onChange={(e) => setToPeriod(e.target.value)} data-testid="stmt-to" />
              </div>
            </>
          )}
          <Button size="sm" variant="outline" className="h-8" onClick={load} disabled={loading} data-testid="stmt-apply">Apply</Button>
        </div>
      )}

      {cmp && (
        <div className="dc-card p-3 flex flex-wrap items-center gap-4" data-testid="stmt-comparison-strip">
          <div className="overline">Legacy ↔ GL check · {cmp.label}</div>
          <div className="text-xs font-mono-alt tabular-nums">GL: <b>{money(cmp.gl)}</b></div>
          <div className="text-xs font-mono-alt tabular-nums">Legacy: <b>{money(cmp.legacy)}</b></div>
          <div className={`text-xs font-semibold ${Math.abs(cmp.gl - cmp.legacy) <= 0.01 ? "text-emerald-600" : "text-amber-600"}`}>
            Δ {money(cmp.gl - cmp.legacy)}
          </div>
          <div className="text-[10px] text-slate-400">Differences are expected — GL includes AR/AP accruals & payroll journals the flat ledger never captured.</div>
        </div>
      )}

      <div className="dc-card p-4">
        {loading ? (
          <div className="font-mono-alt text-xs uppercase tracking-widest text-slate-400">Loading…</div>
        ) : source === "gl" ? (
          <>
            {view !== "cf" && (
              <div className="mb-3 text-[10px] text-slate-400">Click any account line to open its ledger (the transactions behind the number).</div>
            )}
            {view === "is" ? <GLIncomeStatement data={gl} onRow={openDrill} /> : view === "bs" ? <GLBalanceSheet data={gl} onRow={openDrill} /> : view === "eq" ? <GLEquityChanges data={gl} onRow={openDrill} /> : <GLCashFlow data={gl} />}
          </>
        ) : (
          view === "eq" ? <EmptyState icon={ChartLineUp} title="No legacy equity statement" description="The Statement of Changes in Equity is generated from the GL only." />
          : view === "is" ? <LegacyIS audit={legacyAudit} /> : view === "bs" ? <LegacyBS audit={legacyAudit} /> : <LegacyCF cf={legacyCf} />
        )}
      </div>

      <AccountLedgerDialog drill={drill} onClose={() => setDrill(null)} />
    </div>
  );
}
